import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import flow from 'lodash/flow';
import { createStructuredSelector } from 'reselect';
import { List, Map } from 'immutable';

import {
  EditorState,
  ContentState,
  RichUtils,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';

import Editor from 'draft-js-plugins-editor';
import MultiDecorator from 'draft-js-plugins-editor/lib/Editor/MultiDecorator';
import createInlineToolbarPlugin from 'draft-js-inline-toolbar-plugin';
import LinkAdd from 'draft-js-linkify-plugin/lib/LinkAdd';
import './draftJsLinkifyPlugin.css';

import { consumeContext } from '../../utils/context';
import { getMaxLength } from '../../utils/schema';
import getCharCount from '../../utils/getCharCount';

import createLinkDecorator from '../../draft-js/decorators/Link';
import createSpellingDecorator from '../../draft-js/decorators/Spelling';
import blockIsListItem from '../../draft-js/utils/blockIsListItem';
import blockRenderMap from '../../draft-js/blockRenderMap';
import { getKeyCommands } from '../../draft-js/keyCommands';
import { getPasteSupport } from '../../draft-js/pasteSupport';
import { getStyleMap } from '../../draft-js/styleMap';
import { getToolbarButtons } from '../../draft-js/toolbarButtons';

import { selectEditorReadOnly, selectForceUpdateTextBlocks } from '../../containers/EditorContainer/selectors';
import makeSelectSpellCheck from '../../containers/SpellCheck/selectors';

import TextLength from '../TextLength';

import Wrapper from './Wrapper';
import EditorWrapper from './EditorWrapper';
import Placeholder from './Placeholder';

const MSWordParagraphDelimiter = '<p class=MsoNormal><o:p>&nbsp;</o:p></p>';

export class TextBlock extends React.Component {
  constructor(props) {
    super(props);

    this.initState();

    const disabledInlineStyles = [];
    const disabledKeyCommands = [];
    const disabledButtons = [];

    if (this.props.disableBold) {
      disabledInlineStyles.push('BOLD');
      disabledKeyCommands.push('bold');
      disabledButtons.push('bold');
    }

    // Editor config
    this.blockRenderMap = blockRenderMap;
    this.keyCommands = getKeyCommands({ disabledKeyCommands });
    this.pasteSupport = getPasteSupport({ disabledInlineStyles });
    this.styleMap = getStyleMap({ disabledInlineStyles });

    // Editor plugins
    this.inlineToolbarElement = null;
    this.linkAddElement = null;
    this.inlineToolbarPlugin = createInlineToolbarPlugin({
      structure: getToolbarButtons({ disabledButtons }),
      theme: {
        buttonStyles: {
          active: 'draftJsToolbar__active',
          button: 'draftJsToolbar__button',
          buttonWrapper: 'draftJsToolbar__buttonWrapper',
        },
        toolbarStyles: {
          toolbar: 'draftJsToolbar__toolbar',
        },
      },
      addLink: () => {
        if (this.linkAddElement) {
          this.linkAddElement.openPopover();
        }
      },
    });

    this.plugins = [this.inlineToolbarPlugin];
  }

  componentDidMount() {
    if (this.props.autoFocus && !window.App.disableAutoFocus) {
      this.props.onAddNewInput(this.editor);
    } else if (window.App.lastCreatedOnPasteBlockIndex === this.props.blockIndex) {
      this.props.onAddNewInput(this.editor);

      window.App.disableAutoFocus = false;
      window.App.lastCreatedOnPasteBlockIndex = null;
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.forceUpdateTextBlocks && !this.props.forceUpdateTextBlocks) {
      // re-init editor state with new content from redux
      this.initState(nextProps);
    }

    if (
      this.props.block.get('spellings') !== nextProps.block.get('spellings')
      || this.props.globalSpellings !== nextProps.globalSpellings
    ) {
      setTimeout(this.reApplyDecorators, 0);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.editorState !== this.state.editorState) {
      return true;
    }
    if (nextProps.readOnly !== this.props.readOnly) {
      return true;
    }

    if (nextProps.maxLength !== this.props.maxLength) {
      return true;
    }

    return false;
  }

  get maxLength() {
    if (this.props.maxLength) return this.props.maxLength;
    return getMaxLength(this.props.block.get('type'), this.props.fieldName);
  }

  // https://stackoverflow.com/a/46071494
  getLengthOfSelectedText = () => {
    const currentSelection = this.state.editorState.getSelection();
    const isCollapsed = currentSelection.isCollapsed();

    let length = 0;

    if (!isCollapsed) {
      const currentContent = this.state.editorState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const startBlock = currentContent.getBlockForKey(startKey);
      const isStartAndEndBlockAreTheSame = startKey === endKey;
      const startBlockTextLength = startBlock.getLength();
      const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset();
      const endSelectedTextLength = currentSelection.getEndOffset();
      const keyAfterEnd = currentContent.getKeyAfter(endKey);
      if (isStartAndEndBlockAreTheSame) {
        length += currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        let currentKey = startKey;

        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }

          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }

    return length;
  }

  createDecorator = () => new MultiDecorator(this.editorDecorators);

  handleChange = (editorState) => {
    let newEditorState = this.handleDoubleLinebreak(editorState);

    if (this.pastedFewParagraphs) {
      this.pastedFewParagraphs = false;
      newEditorState = this.splitPastedContent(this.state.editorState, newEditorState);
      this.editor.blur();
    }

    const contentState = newEditorState.getCurrentContent();
    const blockContent = this.props.plainText ? contentState.getPlainText() : contentState;

    this.setState({
      editorState: EditorState.set(newEditorState, { decorator: this.decorator }),
      length: getCharCount(contentState.getPlainText()),
    });

    this.props.onChange(blockContent);
  }

  handleBeforeInput = () => {
    const currentContent = this.state.editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    const selectedTextLength = this.getLengthOfSelectedText();

    if (currentContentLength - selectedTextLength > this.maxLength - 1) {
      return 'handled';
    }
  }

  handleContentStateChange = (contentState) => {
    const newEditorState = EditorState.set(this.state.editorState, { currentContent: contentState });
    this.handleChange(newEditorState);
    this.reApplyDecorators();
  }

  /**
   * При нажатии Enter 2 раза:
   * создать новый текстовый блок под текущим и переместить на него фокус;
   * если после курсора был текст -  перенести его в новый блок.
   *
   * @param  {EditorState} editorState
   * @return {EditorState}
   */
  handleDoubleLinebreak = (editorState) => {
    if (this.props.block.get('type') !== 'TEXT') {
      return editorState;
    }

    const contentState = editorState.getCurrentContent();

    if (contentState.getBlockMap().size < 3) {
      return editorState;
    }

    const rawContent = convertToRaw(contentState);
    const blocksToKeep = [];
    const blocksToMove = [];
    let firstEmptyDraftBlockIndex;

    rawContent.blocks.forEach((block, index) => {
      if (!firstEmptyDraftBlockIndex && block.text.length) {
        blocksToKeep.push(block);
        return;
      }

      if (firstEmptyDraftBlockIndex) {
        if (index === firstEmptyDraftBlockIndex + 1) {
          return;
        }

        blocksToMove.push(block);
        return;
      }

      // пустой draft-js блок,
      if (
        rawContent.blocks[index - 1] && rawContent.blocks[index - 1].text.length && // перед которым есть непустой блок
        rawContent.blocks[index + 1] && !rawContent.blocks[index + 1].text.length // и следом за которым расположен другой пустой блок
      ) {
        firstEmptyDraftBlockIndex = index;
      }
    });

    if (!firstEmptyDraftBlockIndex) {
      return editorState;
    }

    const newBlockType = 'TEXT';
    const newBlockIndex = this.props.blockIndex + 1;
    const newContentState = convertFromRaw({ ...rawContent, blocks: blocksToKeep });

    if (blocksToMove.length) {
      const contentStateToMove = { ...rawContent, blocks: blocksToMove };
      this.props.createBlock(newBlockType, newBlockIndex, { value: contentStateToMove });
    } else {
      this.props.createBlock(newBlockType, newBlockIndex);
    }

    const newEditorState = EditorState.set(editorState, { currentContent: newContentState });

    return EditorState.moveSelectionToEnd(newEditorState);
  }

  handleKeyCommand = (command) => {
    if (!this.keyCommands.includes(command)) {
      return true;
    }

    const { editorState } = this.state;
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.handleChange(newState);
      return true;
    }

    return false;
  }

  handlePastedText = (text, html, callback) => {
    const currentContent = this.state.editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    const selectedTextLength = this.getLengthOfSelectedText();

    if (currentContentLength + text.length - selectedTextLength > this.maxLength) {
      return 'handled';
    }

    if (
      /.*\n.+/.test(text)
      || (html && html.indexOf(MSWordParagraphDelimiter > -1))
    ) {
      this.pastedFewParagraphs = true;
    }

    if (html) {
      const modifiedHtml = html
        // handle MS Word unordered list [19106]
        .replace(/<!\[if !supportLists\]>[\s\S]*?·?[\s\S]*?<!\[endif\]>([\s\S]*?<o:p>)/g, '<li>$1</li>')
        // handle MS Word paragraphs [24146]
        .replace(MSWordParagraphDelimiter, '<br>');

      callback(text, modifiedHtml);
    }
  }

  handleSelectionChange = (selectionState) => {
    const newEditorState = EditorState.forceSelection(this.state.editorState, selectionState);
    this.handleChange(newEditorState);
  }

  initState = (props = this.props) => {
    let editorState;
    let contentState;
    let length = 0;

    const blockContent = props.block.get(props.fieldName);

    if (typeof blockContent === 'string') {
      if (blockContent) {
        contentState = ContentState.createFromText(blockContent);
        length = getCharCount(blockContent);
      }
    } else if (blockContent instanceof Map) {
      contentState = convertFromRaw(blockContent.toJS());
      length = getCharCount(contentState.getPlainText());
    } else if (blockContent instanceof ContentState) {
      contentState = blockContent;
      length = getCharCount(contentState.getPlainText());
    }

    this.editorDecorators = [
      createLinkDecorator.call(this),
      createSpellingDecorator.call(this),
    ];

    this.decorator = this.createDecorator();

    if (contentState) {
      editorState = this.state && this.state.editorState
        ? EditorState.push(this.state.editorState, contentState)
        : EditorState.createWithContent(contentState, this.decorator);

      editorState = EditorState.moveSelectionToEnd(editorState);
    } else {
      editorState = EditorState.createEmpty(this.decorator);
    }

    const newState = { editorState, length };

    // для вызова в конструкторе и вне
    if (this.state) {
      this.setState(newState);
    } else {
      // eslint-disable-next-line react/no-direct-mutation-state
      this.state = newState;
    }
  }

  reApplyDecorators = () => {
    this.setState(prevState => ({
      editorState: EditorState.set(prevState.editorState, { decorator: this.createDecorator() }),
    }));
  }

  /**
   * При paste в текстовый блок нескольких абзацев - разбивать абзацы по блокам.
   *
   * @param  {EditorState} currentEditorState
   * @param  {EditorState} editorState
   * @return {EditorState}
   */
  splitPastedContent = (currentEditorState, editorState) => {
    if (this.props.block.get('type') !== 'TEXT') {
      return editorState;
    }

    const currentContentState = currentEditorState.getCurrentContent();
    const currentContentBlocksCount = currentContentState.getBlockMap().size;
    const contentState = editorState.getCurrentContent();

    if (contentState.getBlockMap().size - currentContentBlocksCount < 1) {
      return editorState;
    }

    const currentFocusKey = currentEditorState.getSelection().focusKey;
    const startMoveFromBlockKey = contentState.getBlockAfter(currentFocusKey).getKey();
    const blockMapToKeep = [];
    const blocksToMove = [];
    let firstBlockToMoveReached = false;
    let isListSequence = false;

    contentState.getBlockMap().forEach((block) => {
      if (!firstBlockToMoveReached && startMoveFromBlockKey === block.getKey()) {
        firstBlockToMoveReached = true;
      }

      if (firstBlockToMoveReached && !(isListSequence && blockIsListItem(block))) {
        blocksToMove.push(block);
        isListSequence = false;
      } else {
        blockMapToKeep.push(block);
        isListSequence = !blocksToMove.length && blockIsListItem(block);
      }
    });

    const newEditorState = EditorState.set(editorState, {
      currentContent: ContentState.createFromBlockArray(blockMapToKeep),
    });

    window.App.disableAutoFocus = true;
    for (let i = 0; i < blocksToMove.length; i += 1) {
      const nextBlockIndex = this.props.blockIndex + i + 1;
      const block = blocksToMove[i];
      const blockMap = [block];

      // не разбивать списки на несколько блоков
      if (blockIsListItem(block)) {
        while (i < blocksToMove.length - 1 && blockIsListItem(blocksToMove[i + 1])) {
          const nextBlock = blocksToMove[i + 1];
          blockMap.push(nextBlock);
          i += 1;
        }
      }

      const value = ContentState.createFromBlockArray(blockMap);

      // установить фокус в последнем создаваемом блоке
      if (i === blocksToMove.length - 1) {
        window.App.lastCreatedOnPasteBlockIndex = nextBlockIndex;
      }

      this.props.createBlock('TEXT', nextBlockIndex, { value });
    }

    return EditorState.moveSelectionToEnd(newEditorState);
  }

  render() {
    const { InlineToolbar } = this.inlineToolbarPlugin;

    return (
      <Wrapper className={this.props.fieldName}>
        {!this.state.length && (
          <Placeholder className="placeholder">{this.props.placeholder}</Placeholder>
        )}

        <EditorWrapper>
          <Editor
            editorState={this.state.editorState}
            blockRenderMap={this.blockRenderMap}
            customStyleMap={this.styleMap}
            // strip rich styling on paste:
            //   - if we need plain text
            //   - if there's a custom styleMap
            // TODO https://github.com/facebook/draft-js/issues/166
            stripPastedStyles={this.props.plainText || this.styleMap}
            pasteSupport={this.pasteSupport}
            plugins={this.plugins}
            decorators={this.editorDecorators}
            readOnly={this.props.readOnly}
            onChange={this.handleChange}
            handleBeforeInput={this.handleBeforeInput}
            handleKeyCommand={this.handleKeyCommand} // eslint-disable-line react/jsx-handler-names
            handlePastedText={this.handlePastedText} // eslint-disable-line react/jsx-handler-names
            onFocus={this.props.onFocusBlock}
            ref={(editor) => { this.editor = editor; }}
            spellCheck
          />

          <InlineToolbar ref={(el) => { this.inlineToolbarElement = el; }} />
        </EditorWrapper>

        <LinkAdd
          linkifyOptions={{ fuzzyLink: false }}
          ref={(el) => { this.linkAddElement = el; }}
          editorState={this.state.editorState}
          onChange={this.handleChange}
          inlineToolbarElement={this.inlineToolbarElement}
          placeholder="Вставьте url…"
          showAddButton={false}
        />

        {!this.props.plainText && (
          <TextLength length={this.state.length} limit={this.maxLength} />
        )}
      </Wrapper>
    );
  }
}

TextBlock.propTypes = {
  block: PropTypes.instanceOf(Map).isRequired,
  blockIndex: PropTypes.number,

  fieldName: PropTypes.string,
  placeholder: PropTypes.string,

  globalSpellings: PropTypes.instanceOf(List),

  autoFocus: PropTypes.bool,
  disableBold: PropTypes.bool,
  forceUpdateTextBlocks: PropTypes.bool,
  plainText: PropTypes.bool,
  readOnly: PropTypes.bool,
  maxLength: PropTypes.number,

  onAddNewInput: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onFocusBlock: PropTypes.func,
  createBlock: PropTypes.func,
};

TextBlock.defaultProps = {
  fieldName: 'value',
  plainText: false,
  autoFocus: false,
  maxLength: 0,
};

const mapStateToProps = createStructuredSelector({
  readOnly: selectEditorReadOnly(),
  forceUpdateTextBlocks: selectForceUpdateTextBlocks(),
  globalSpellings: makeSelectSpellCheck(),
});

export default flow(
  consumeContext({
    blockIndex: PropTypes.number,
    onAddNewInput: PropTypes.func,
    onFocusBlock: PropTypes.func,
    createBlock: PropTypes.func,
  }),
  connect(mapStateToProps)
)(TextBlock);
